import { useCallback, useContext, useEffect, useState, useRef } from 'react';

import { ViewerContext } from '@vimeo/viewer';

import { CallbackProps } from './types';

declare const turnstile: any;

/**
 * Render Turnstile component. This will return a token from Turnstile if user passes
 */
export function useTurnstile(
  isInvisible: boolean = false,
  containerId: string,
  callbacks: CallbackProps = {},
) {
  const turnstileScriptRef = useRef<HTMLScriptElement>();

  const { successCb, beforeInteractiveCb, errorCb } = callbacks;
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileWidgetId, setTurnstileWidgetId] = useState('');
  const [turnstileError, setTurnstileError] = useState('');

  let viewer = useContext(ViewerContext);
  const enableTurnstile = viewer?.isTurnstileEnabled || false;

  const prepareTurnstileWidget = useCallback(() => {
    const widgetId = turnstile.render(`#${containerId}`, {
      sitekey: isInvisible ? viewer?.turnstileSiteKeyInvisible : viewer?.turnstileSiteKey,
      language: viewer?.locale ?? 'en',
      callback: function (token: string) {
        setTurnstileToken(token);
        if (successCb) {
          successCb(token);
        }
      },
      'expired-callback': function () {
        // refresh token
        setTurnstileToken('');
        turnstile.reset(`#${containerId}`);
        turnstile.execute(`#${containerId}`);
      },
      'retry-interval': 120000, // 2 minutes
      'error-callback': function (errorMsg: string) {
        setTurnstileError(errorMsg);
        if (errorCb) {
          errorCb();
        }
      },
      'before-interactive-callback': function () {
        if (beforeInteractiveCb) {
          beforeInteractiveCb();
        }
      },
    });

    setTurnstileWidgetId(widgetId);
  }, [
    beforeInteractiveCb,
    containerId,
    errorCb,
    isInvisible,
    successCb,
    viewer?.locale,
    viewer?.turnstileSiteKey,
    viewer?.turnstileSiteKeyInvisible,
  ]);

  useEffect(() => {
    if (enableTurnstile && !turnstileScriptRef?.current) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'; // Replace with your site key
      script.async = true;
      script.defer = true;

      script.onload = prepareTurnstileWidget;

      document.body.appendChild(script);
      turnstileScriptRef.current = script;

      return () => {
        if (turnstileScriptRef?.current) {
          document.body.removeChild(turnstileScriptRef?.current);
        }
      };
    }
    if (!enableTurnstile) {
      setTurnstileToken('turnstile_disabled');
    }
  }, [enableTurnstile]);

  if (!enableTurnstile) {
    return {
      turnstileToken,
    };
  }

  return {
    turnstileError,
    turnstileToken,
    setTurnstileToken,
    turnstileWidgetId,
    setTurnstileWidgetId,
    prepareTurnstileWidget,
  };
}
